import { Filters, SelectFilterConfig } from '@cimplico/components';
import { useEffect, useMemo, useState } from 'react';
import PageTitle from '../components/PageTitle';
import PrimaryPageActions from '../components/primary-page-actions/PrimaryPageActions';
import SecondaryPageActions from '../components/secondary-page-actions/SecondaryPageActions';
import ClientQueriesTable from '../features/client-queries/ClientQueriesTable';
import ClientQueryActiveConfirm from '../features/client-queries/ClientQueryActiveConfirm';
import ClientQueryDeleteConfirm from '../features/client-queries/ClientQueryDeleteConfirm';
import ClientQueryDetails from '../features/client-queries/ClientQueryDetails';
import NewOrEditClientQuery from '../features/client-queries/NewOrEditClientQuery';
import { useWorkpaperContext } from '../providers/WorkpaperProvider';
import { useUpdateClientQueries } from '../queries/use-update-client-queries';
import { useUsers } from '../queries/use-users';
import { useWorksheets } from '../queries/use-worksheets';
import { ClientQuery, ClientQueryOverview, ClientQueryStatusEnum } from '../types/client-query';
import { toastNotification } from '../components/toast/Toast';
import { saveSort, saveFilter } from '../slices/workpaperFilterSlice';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import { useSearchParams } from 'react-router-dom';

const sortOptions = [
    { id: 'updatedAt', label: 'Last Updated' },
    { id: 'status', label: 'Status' },
    { id: 'replyCount', label: 'Replies' },
];

function ClientQueriesPage(): JSX.Element {
    const { id: workpaperId, readOnly } = useWorkpaperContext();
    const [params, setParams] = useSearchParams();

    const [selectedClientQueryId, setSelectedClientQueryId] = useState<string | undefined>(params.get('activeQuery') || undefined);
    const [checkedClientQueries, setCheckedClientQueries] = useState<{ id: string; title: string; context: string }[]>([]);
    const [newQuery, setNewQuery] = useState(false);
    const [activeClientQueryId, setActiveClientQueryId] = useState<string | undefined>();
    const [deleteClientQueryId, setDeleteClientQueryId] = useState<string | undefined>();
    const [editClientQuery, setEditClientQuery] = useState<ClientQuery | ClientQueryOverview | undefined>();
    const [showContent, setShowContent] = useState(Boolean(params.get('activeQuery')));

    const [sort, setSort] = useState<string | undefined>();
    const [filter, setFilter] = useState<string | undefined>();
    const [search, setSearch] = useState<string | undefined>('');

    const updateClientQueriesQuery = useUpdateClientQueries(workpaperId);

    const savedFilters = useSelector((state: RootState) => state.workpaperFilters.value[workpaperId]?.clientQueries);
    const dispatch = useDispatch();

    const worksheetQuery = useWorksheets({ page: 1, searchQuery: search, filterQuery: '', sortQuery: '' });
    const usersQuery = useUsers({ page: 1, search: '', sort: '', filter: '' });

    useEffect(() => {
        const activeQueryId = params.get('activeQuery');
        if (activeQueryId !== null) {
            setSelectedClientQueryId(activeQueryId);
        }
    }, [params]);

    function handleSortChange(qs: string, sort: { order: 'asc' | 'desc'; option?: string }): void {
        dispatch(saveSort({ workpaperId, section: 'clientQueries', sort, qs }));
        setSort(qs);
    }

    function handleFilterChange(qs: string, filters: Filters): void {
        dispatch(saveFilter({ workpaperId, section: 'clientQueries', filters, qs }));
        setFilter(qs);
    }

    const context = savedFilters?.filter?.find((f) => f.id === 'context' && f.type === 'select') as SelectFilterConfig | undefined;
    const author = savedFilters?.filter?.find((f) => f.id === 'author' && f.type === 'select') as SelectFilterConfig | undefined;

    const worksheetOptions = useMemo(() => {
        return (
            worksheetQuery?.data?.worksheets.map((w) => ({
                label: `${w.name}`,
                value: w.id,
                active: context?.options.find((o) => o.value === w.id && o.active) !== undefined,
            })) || []
        );
    }, [context?.options, worksheetQuery?.data?.worksheets]);

    const filterOptions: Filters = useMemo(
        () => [
            savedFilters?.filter?.find((f) => f.id === 'status') ?? {
                type: 'checkbox',
                title: 'Status',
                options: [
                    { active: false, label: 'Active', value: 'ACTIVE' },
                    { active: false, label: 'Resolved', value: 'RESOLVED' },
                    { active: false, label: 'Draft', value: 'DRAFT' },
                ],
                id: 'status',
            },
            savedFilters?.filter?.find((f) => f.id === 'type') ?? {
                type: 'checkbox',
                title: 'Context Type',
                options: [
                    { active: false, label: 'General', value: 'GENERAL' },
                    { active: false, label: 'Worksheets', value: 'WORKSHEETS' },
                ],
                id: 'type',
            },
            savedFilters?.filter?.find((f) => f.id === 'context') ?? {
                id: 'context',
                isMulti: true,
                options: [...worksheetOptions],
                title: 'Context',
                type: 'select',
            },
            savedFilters?.filter?.find((f) => f.id === 'updatedAt') ?? {
                type: 'date',
                title: 'Last Updated',
                option: {},
                id: 'updatedAt',
            },
            savedFilters?.filter?.find((f) => f.id === 'replyCount') ?? {
                type: 'numeric',
                title: 'Replies',
                option: {},
                id: 'replyCount',
                isPercent: false,
            },
            {
                id: 'author',
                isMulti: true,
                options: (usersQuery.data?.users ?? []).map((user) => ({
                    active: author?.options.find((o) => o.value === user.id && o.active) !== undefined,
                    label: `${user.firstName} ${user.lastName}`,
                    value: user.id,
                    // decoration: <UserAvatar size="xs" user={user} />,
                })),
                title: 'Author',
                type: 'select',
            },
        ],
        [author?.options, savedFilters?.filter, usersQuery.data?.users, worksheetOptions]
    );

    function handleRowSelect(id: string): void {
        if (newQuery) {
            setNewQuery(false);
        }
        if (editClientQuery) {
            setEditClientQuery(undefined);
        }
        if (selectedClientQueryId === id) {
            setSelectedClientQueryId(undefined);
            setParams({});
        } else {
            setSelectedClientQueryId(id);
            setParams({ activeQuery: id });
        }
    }

    function handleRowChecked(id: string, title: string, context: string): void {
        if (checkedClientQueries.map((cq) => cq.id).includes(id)) {
            setCheckedClientQueries((prev) => [...prev.filter((value) => value.id !== id)]);
        } else {
            setCheckedClientQueries((prev) => [...prev, { id, title, context }]);
        }
    }

    function handleResolve(id: string): void {
        updateClientQueriesQuery.mutate([{ id, fields: { status: ClientQueryStatusEnum.RESOLVED } }], {
            onSuccess: () => {
                toastNotification('success', 'Client query marked as resolved!');
            },
        });
    }

    function handleActive(id: string): void {
        updateClientQueriesQuery.mutate([{ id, fields: { status: ClientQueryStatusEnum.ACTIVE } }]);
    }

    function downloadCsvFile(): void {
        const allCheckedClientQueries = [];
        for (const cq of checkedClientQueries) {
            const clientQuery = [`"${cq.title.replaceAll('\n', '\r\n')}", "${cq.context}", ""`];
            allCheckedClientQueries.push(clientQuery);
            handleActive(cq.id);
        }
        setCheckedClientQueries([]);

        const CSV = [['"Title","Context","Response"'], allCheckedClientQueries.join('\n')].join('\n');
        const file = new Blob([CSV], { type: 'text/csv' });

        const element = document.createElement('a');
        element.href = URL.createObjectURL(file);
        element.download = 'query-list.csv';
        document.body.appendChild(element);
        element.click();
    }

    function handleEditClientQuery(query: ClientQuery | ClientQueryOverview): void {
        setNewQuery(false);
        setSelectedClientQueryId(undefined);
        if (editClientQuery?.id !== query.id) {
            setEditClientQuery(query);
        }
    }

    return (
        <>
            {/* ACTIVE CONFIRMATION */}
            {activeClientQueryId && (
                <ClientQueryActiveConfirm
                    clientQueryId={activeClientQueryId}
                    workpaperId={workpaperId}
                    onClose={(): void => setActiveClientQueryId(undefined)}
                />
            )}
            {/* DELETE CONFIRMATION */}
            {deleteClientQueryId && (
                <ClientQueryDeleteConfirm
                    clientQueryId={deleteClientQueryId}
                    workpaperId={workpaperId}
                    onClose={(): void => setDeleteClientQueryId(undefined)}
                    onDelete={(): void => {
                        setDeleteClientQueryId(undefined);
                        setSelectedClientQueryId(undefined);
                        setEditClientQuery(undefined);
                    }}
                />
            )}
            <div className="pb-4">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-8">
                    <PageTitle title="Client Queries" />
                    {checkedClientQueries.length > 0 && (
                        <SecondaryPageActions>
                            <SecondaryPageActions.Button
                                type="primary"
                                label={`Download Query List (${checkedClientQueries.length})`}
                                onClick={downloadCsvFile}
                                disabled={readOnly}
                            />
                        </SecondaryPageActions>
                    )}
                    {checkedClientQueries.length === 0 && (
                        <PrimaryPageActions
                            sortOptions={sortOptions}
                            filterOptions={filterOptions}
                            id="client-queries"
                            onFilterChange={handleFilterChange}
                            onPrimaryButtonClick={(): void => {
                                setNewQuery(true);
                                setSelectedClientQueryId(undefined);
                            }}
                            onSearchChange={setSearch}
                            onSortChange={handleSortChange}
                            primaryButtonLabel="New Query"
                            defaultSortOption={savedFilters?.sort?.option}
                            defaultSortOrder={savedFilters?.sort?.order}
                            disablePrimaryButton={readOnly}
                            disableSecondaryButton={readOnly}
                        />
                    )}
                </div>
                <div className="grid mt-4" style={{ gridTemplateColumns: '1fr min-content' }}>
                    <section className={`${selectedClientQueryId || newQuery || editClientQuery ? 'border-r' : ''} `}>
                        <ClientQueriesTable
                            checkedRows={checkedClientQueries.map((cq) => cq.id)}
                            onRowChecked={handleRowChecked}
                            onRowSelect={handleRowSelect}
                            showTempRow={newQuery}
                            onRowActive={setActiveClientQueryId}
                            onRowResolve={handleResolve}
                            onRowDelete={setDeleteClientQueryId}
                            onRowEdit={handleEditClientQuery}
                            sort={sort}
                            search={search}
                            filter={filter}
                            onNew={(): void => setNewQuery(true)}
                            hideEmptyState={newQuery}
                        />
                    </section>
                    <section
                        className={`${selectedClientQueryId || newQuery || editClientQuery ? 'w-[750px]' : 'w-[0px]'}`}
                        style={{ transition: 'width 250ms' }}
                        onTransitionEnd={(e): void => {
                            // Transitons in children also trigger this callback so we check for specifically a width transition.
                            if (e.propertyName === 'width') {
                                setShowContent(!showContent);
                            }
                        }}
                    >
                        {' '}
                        <div className={showContent ? 'block' : 'hidden'}>
                            {selectedClientQueryId && (
                                <ClientQueryDetails
                                    clientQueryId={selectedClientQueryId}
                                    workpaperId={workpaperId}
                                    onActive={setActiveClientQueryId}
                                    onResolve={handleResolve}
                                    onDelete={setDeleteClientQueryId}
                                    onEdit={handleEditClientQuery}
                                    onClose={(): void => {
                                        setParams({});
                                        setSelectedClientQueryId(undefined);
                                    }}
                                />
                            )}
                            {newQuery && (
                                <NewOrEditClientQuery
                                    workpaperId={workpaperId}
                                    onClose={(): void => {
                                        setSelectedClientQueryId(undefined);
                                        setParams({});
                                        setNewQuery(false);
                                    }}
                                    onCreate={(id: string): void => {
                                        setSelectedClientQueryId(id);
                                        setNewQuery(false);
                                        setParams({ activeQuery: id });
                                    }}
                                />
                            )}
                            {editClientQuery && (
                                <NewOrEditClientQuery
                                    workpaperId={workpaperId}
                                    defaultClientQuery={editClientQuery}
                                    isEditing
                                    onClose={(): void => {
                                        setEditClientQuery(undefined);
                                    }}
                                    onCreate={(id: string): void => {
                                        setSelectedClientQueryId(id);
                                        setEditClientQuery(undefined);
                                    }}
                                />
                            )}
                        </div>
                    </section>
                </div>
            </div>
        </>
    );
}
export default ClientQueriesPage;
